import React, { useContext } from 'react';
import { Table, Tag, Typography, Space } from 'antd';
import { CalculatorOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useIdColumn } from '../../../hooks/useIdColumn';
import useAuth from '../../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import { BalanceHistoryEntry } from '../interfaces';
import { convertMoedaBRDoJSNovo, formatDateStringToDateAndTime } from '../../../utils/Conversores';
import { TicketTags } from '../../../components/TicketTags';
import { ThemeContext } from '../../../contexts/ThemeContext';

const { Text } = Typography;

interface BalanceHistoryCardProps {
  history: BalanceHistoryEntry[];
}

const CalcStep: React.FC<{
  label: string;
  children: React.ReactNode;
  emphasis?: 'default' | 'primary' | 'success' | 'error';
  isDark: boolean;
}> = ({ label, children, emphasis = 'default', isDark }) => {
  const color =
    emphasis === 'primary'
      ? '#1677ff'
      : emphasis === 'success'
        ? '#52c41a'
        : emphasis === 'error'
          ? '#ff4d4f'
          : isDark ? '#f1f5f9' : '#1e293b';

  return (
    <div
      style={{
        textAlign: 'center',
        minWidth: 100,
        padding: '8px 12px',
        borderRadius: 8,
        background: isDark ? '#262626' : '#ffffff',
        border: `1px solid ${isDark ? '#3a3a3a' : '#e2e8f0'}`,
        boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', marginBottom: 3, fontWeight: 500 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color, whiteSpace: 'nowrap' }}>
        {children}
      </div>
    </div>
  );
};

const CalcBreakdown: React.FC<{ entry: BalanceHistoryEntry; isDark: boolean }> = ({ entry, isDark }) => {
  const bilhetesEsperados = Math.floor(entry.saldoAposSoma / entry.divisor);
  const divergente = bilhetesEsperados !== entry.bilhetesGerados;

  const arrow = (
    <ArrowRightOutlined
      style={{
        color: isDark ? '#64748b' : '#94a3b8',
        fontSize: 13,
        flexShrink: 0,
      }}
    />
  );

  return (
    <div
      style={{
        background: isDark ? '#1a1a1a' : '#f8fafc',
        border: `1px solid ${isDark ? '#2a2a2a' : '#eaedf1'}`,
        borderRadius: 10,
        padding: '14px 16px',
        width: '100%',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#e2e8f0' : '#334155', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
        <CalculatorOutlined style={{ color: '#1677ff' }} />
        <span>Detalhamento do Cálculo de Bilhetes (Ordem #{entry.ordem})</span>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10,
          rowGap: 10,
        }}
      >
        <CalcStep label="Saldo anterior" isDark={isDark}>
          {convertMoedaBRDoJSNovo(entry.saldoAnterior)}
        </CalcStep>

        <Text style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#64748b' : '#94a3b8' }}>+</Text>

        <CalcStep label="Valor considerado" isDark={isDark}>
          {convertMoedaBRDoJSNovo(entry.valorConsiderado)}
        </CalcStep>

        {arrow}

        <CalcStep label="Saldo após soma" emphasis="primary" isDark={isDark}>
          {convertMoedaBRDoJSNovo(entry.saldoAposSoma)}
        </CalcStep>

        {arrow}

        <CalcStep label="Divisão" isDark={isDark}>
          <Text code style={{ fontSize: 12, color: isDark ? '#93c5fd' : '#0958d9', background: 'transparent' }}>
            floor(saldo / {entry.divisor})
          </Text>
        </CalcStep>

        {arrow}

        <CalcStep label="Bilhetes gerados" emphasis={divergente ? 'error' : 'success'} isDark={isDark}>
          {entry.bilhetesGerados}
          {divergente && (
            <Tag color="error" style={{ marginLeft: 6, fontWeight: 400 }}>
              esp: {bilhetesEsperados}
            </Tag>
          )}
        </CalcStep>

        {arrow}

        <CalcStep label="Saldo restante" isDark={isDark}>
          {convertMoedaBRDoJSNovo(entry.saldoRestante)}
        </CalcStep>
      </div>

      {entry.bilhetesAssociados.length > 0 && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px dashed ${isDark ? '#333333' : '#e2e8f0'}`,
          }}
        >
          <Text style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b' }}>
            Bilhetes gerados nesta nota:
          </Text>
          <div style={{ marginTop: 6 }}>
            <Space wrap size={[4, 4]}>
              <TicketTags tickets={entry.bilhetesAssociados.map((ticket) => Number(ticket))} />
            </Space>
          </div>
        </div>
      )}
    </div>
  );
};

export const BalanceHistoryCard: React.FC<BalanceHistoryCardProps> = ({ history }) => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';
  const navigate = useNavigate();

  const { user } = useAuth();
  const isAdmin = user?.role?.id === 1;

  const idColumn = useIdColumn({
    show: isAdmin,
    ref: undefined,
    sorter: false,
    onClick: (id) => navigate(`/nota/detalhes/${id}`),
  });

  const columns = [
    {
      title: 'Ordem',
      dataIndex: 'ordem',
      key: 'ordem',
      width: 80,
      fixed: 'left' as const,
      render: (ordem: number) => (
        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>#{ordem}</span>
      ),
    },
    idColumn,
    {
      title: 'Data da Nota',
      dataIndex: 'data',
      key: 'data',
      render: formatDateStringToDateAndTime,
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor',
      key: 'valor',
      render: (v: number) => (
        <span style={{ fontWeight: 600, color: isDark ? '#52c41a' : '#237804', whiteSpace: 'nowrap' }}>
          R$ {convertMoedaBRDoJSNovo(v)}
        </span>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      align: 'center' as const,
      render: (type: string) => {
        let color = 'blue';
        if (type === 'NFC') color = 'purple';
        if (type === 'NFE') color = 'cyan';

        return (
          <Tag
            color={color}
            style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
          >
            {type ?? '--'}
          </Tag>
        );
      },
      width: 100,
    },
    {
      title: 'Saldo Anterior',
      dataIndex: 'saldoAnterior',
      key: 'saldoAnterior',
      render: (v: number) => `R$ ${convertMoedaBRDoJSNovo(v)}`,
    },
    {
      title: 'Valor Considerado',
      dataIndex: 'valorConsiderado',
      key: 'valorConsiderado',
      render: (v: number) => `R$ ${convertMoedaBRDoJSNovo(v)}`,
    },
    {
      title: 'Divisor',
      dataIndex: 'divisor',
      key: 'divisor',
      render: (v: number) => <Tag color="cyan">R$ {convertMoedaBRDoJSNovo(v)}</Tag>,
    },
    {
      title: 'Saldo Após Soma',
      dataIndex: 'saldoAposSoma',
      key: 'saldoAposSoma',
      render: (v: number) => <span style={{ fontWeight: 600, color: '#1677ff' }}>R$ {convertMoedaBRDoJSNovo(v)}</span>,
    },
    {
      title: 'Saldo Restante',
      dataIndex: 'saldoRestante',
      key: 'saldoRestante',
      render: (v: number) => `R$ ${convertMoedaBRDoJSNovo(v)}`,
    },
    {
      title: 'Bilhetes Gerados',
      dataIndex: 'bilhetesAssociados',
      key: 'bilhetesAssociados',
      align: 'center' as const,
      fixed: 'right' as const,
      render: (ids: string[]) => {
        const count = ids ? ids.length : 0;
        if (count === 0) {
          return (
            <Tag color="default" style={{ color: '#8c8c8c', borderRadius: 10, margin: 0, fontWeight: 500 }} bordered={false}>
              0 Bilhetes
            </Tag>
          );
        }

        return (
          <Tag color="#1677ff" style={{ borderRadius: 10, margin: 0, fontWeight: 600 }}>
            {count} {count === 1 ? 'Bilhete' : 'Bilhetes'}
          </Tag>
        );
      },
      width: 130,
    },
  ].filter(Boolean) as ColumnsType<BalanceHistoryEntry>;

  const cardBg = isDark ? '#141414' : '#ffffff';
  const labelColor = isDark ? '#8c8c8c' : '#64748b';
  const textColor = isDark ? '#f0f0f0' : '#1e293b';

  return (
    <div
      style={{
        background: cardBg,
        borderRadius: 14,
        border: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.35)'
          : '0 2px 10px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: `1px solid ${isDark ? '#262626' : '#eaedf1'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          background: isDark ? '#1a1a1a' : '#fafafa',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: isDark ? 'rgba(22, 119, 255, 0.15)' : 'rgba(22, 119, 255, 0.1)',
              color: '#1677ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
            }}
          >
            <CalculatorOutlined />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textColor }}>
              Histórico de Cálculo dos Bilhetes
            </div>
            <div style={{ fontSize: 12, color: labelColor }}>
              Acompanhe a soma de saldos e a geração sequencial de bilhetes deste usuário
            </div>
          </div>
        </div>

        <Tag color="blue" style={{ margin: 0, fontWeight: 600 }}>
          {history.length} cálculo(s) acumulado(s)
        </Tag>
      </div>

      {/* Table */}
      <Table
        className={`modern-data-table ${isDark ? 'modern-data-table-dark' : ''}`}
        columns={columns}
        dataSource={history}
        rowKey="ordem"
        size="small"
        pagination={false}
        scroll={{
          x: 1400,
          y: 'calc(100vh - 450px)',
        }}
        onRow={(record) => ({
          style: record.notaAtual
            ? {
              background: isDark ? 'rgba(250, 173, 20, 0.12)' : '#fffbe6',
            }
            : {},
        })}
        rowClassName={(record) => (record.notaAtual ? 'current-invoice-row' : '')}
        expandable={{
          expandedRowRender: (record) => <CalcBreakdown entry={record} isDark={isDark} />,
          defaultExpandedRowKeys: history.filter((h) => h.notaAtual).map((h) => h.ordem),
        }}
      />
    </div>
  );
};

export default BalanceHistoryCard;